import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { formatTimestamp, formatResponseTime } from "@/utils/helpers";
import { TypingIndicator } from "./Spinner";
import { Copy, Check } from "lucide-react";

// Esquema de sanitizado para el HTML embebido que devuelve el LLM/MCP (p. ej.
// contenido de Moodle con <br>, listas, negritas). Parte del esquema de GitHub
// (que ya permite <br>, tablas y formato básico) y bloquea scripts/handlers.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    td: [...(defaultSchema.attributes?.td || []), "align"],
    th: [...(defaultSchema.attributes?.th || []), "align"],
  },
};

export const Message = ({
  message,
  showMetadata = false,
  messageRef = null,
}) => {
  const { t } = useTranslation();
  const isUser = message.role === "user";
  const isTyping = !isUser && message.isStreaming && !message.content;
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Error al copiar:", err);
    }
  };

  return (
    <div
      ref={messageRef}
      className={`gpc-message ${
        isUser ? "gpc-message-user" : "gpc-message-assistant"
      }`}
      role="article"
      aria-label={
        isUser ? t("ui.message.userMessage") : t("ui.message.assistantMessage")
      }
    >
      {/* Contenido del mensaje */}
      <div className="gpc-message-content">
        {isTyping ? (
          <TypingIndicator />
        ) : isUser ? (
          message.content.trim()
        ) : (
          <ReactMarkdown
            remarkPlugins={[[remarkGfm, { autoLinkLiterals: true }]]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
            components={{
              a: ({ node, ...props }) => (
                <a target="_blank" rel="noopener noreferrer" {...props} />
              ),
              table: ({ node, ...props }) => (
                <div className="gpc-table-wrapper">
                  <table className="gpc-table" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => (
                <thead className="gpc-table-head" {...props} />
              ),
              tbody: ({ node, ...props }) => (
                <tbody className="gpc-table-body" {...props} />
              ),
              tr: ({ node, ...props }) => (
                <tr className="gpc-table-row" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th className="gpc-table-header" {...props} />
              ),
              td: ({ node, ...props }) => (
                <td className="gpc-table-cell" {...props} />
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>

      {/* Timestamp, modelo y botón de copiar */}
      <div
        className={`gpc-message-timestamp ${
          isUser ? "gpc-message-timestamp-user" : ""
        }`}
      >
        {!isUser && message.metadata?.modelUsed && (
          <span className="gpc-model-badge">{message.metadata.modelUsed}</span>
        )}
        <span className="gpc-timestamp-right">
          <span>{formatTimestamp(message.timestamp)}</span>
          {!isTyping && message.content && (
            <button
              type="button"
              onClick={handleCopy}
              className="gpc-copy-button"
              aria-label={copied ? "Copiado" : "Copiar mensaje"}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          )}
        </span>
      </div>

      {/* Metadata (solo para mensajes del asistente) */}
      {!isUser && showMetadata && message.metadata && (
        <div className="gpc-message-metadata">
          {message.metadata.tokensUsed && (
            <div className="gpc-metadata-item">
              <strong>{t("metadata.tokensUsed")}:</strong>{" "}
              {message.metadata.tokensUsed}
            </div>
          )}
          {message.metadata.responseTimeMs && (
            <div className="gpc-metadata-item">
              <strong>{t("metadata.responseTime")}:</strong>{" "}
              {formatResponseTime(message.metadata.responseTimeMs)}
            </div>
          )}
          {message.metadata.modelUsed && (
            <div className="gpc-metadata-item">
              <strong>{t("metadata.model")}:</strong>{" "}
              {message.metadata.modelUsed}
            </div>
          )}
        </div>
      )}
    </div>
  );
};